import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Text } from '@televet/kibble-ui/Text';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toaster } from '@/components/ui/toaster';
import { useState } from 'react';
import { DeleteDialog } from '@/components/DeleteDialog';

export const Route = createFileRoute('/_authenticated/test-suites/')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Test Suites',
    };
  },
});

function RouteComponent() {
  const { data: suitesData } = useQuery(
    trpc.agentTest.listSuites.queryOptions({ page: 1, limit: 100 })
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [suiteToDelete, setSuiteToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const suites = (suitesData?.suites || []) as Array<{
    id: string;
    name: string;
    status: 'Active' | 'Archived';
    agent?: { name: string } | null;
    _count: { testCases: number; testExecutions: number };
  }>;

  const { mutate: deleteSuite, isPending: isDeleting } = useMutation(
    trpc.agentTest.deleteSuite.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Test suite deleted',
          description: data.message,
        });
        setSuiteToDelete(null);
        queryClient.invalidateQueries({
          queryKey: trpc.agentTest.listSuites.pathKey(),
        });
      },
      onError: (error: any) => {
        toaster.error({
          title: 'Failed to delete test suite',
          description: error.message || 'An error occurred',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Flex justify="flex-end">
          <Button onClick={() => navigate({ to: '/test-suites/add' })}>
            Add Test Suite
          </Button>
        </Flex>
        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
              {suites.length === 0 ? (
                <Flex justify="center" align="center" p={8}>
                  <Text>No test suites found</Text>
                </Flex>
              ) : (
                <Table.Root size="md">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Name</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader>Agent</Table.ColumnHeader>
                      <Table.ColumnHeader>Test Cases</Table.ColumnHeader>
                      <Table.ColumnHeader>Executions</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end" width="100px">
                        Actions
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {suites.map((suite) => {
                      const handleRowClick = () => {
                        navigate({ to: `/test-suites/${suite.id}` });
                      };
                      const handleDeleteClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        setSuiteToDelete({
                          id: suite.id,
                          name: suite.name,
                        });
                      };
                      return (
                        <Table.Row key={suite.id}>
                          <Table.Cell cursor="pointer" onClick={handleRowClick}>
                            {suite.name}
                          </Table.Cell>
                          <Table.Cell>
                            <Tag
                              label={suite.status}
                              variant={suite.status === 'Active' ? 'success' : 'default'}
                            />
                          </Table.Cell>
                          <Table.Cell>{suite.agent?.name || '—'}</Table.Cell>
                          <Table.Cell>{suite._count.testCases}</Table.Cell>
                          <Table.Cell>{suite._count.testExecutions}</Table.Cell>
                          <Table.Cell textAlign="end">
                            <Button
                              iconName="trashcan"
                              variant="ghostDestructive"
                              size="sm"
                              onClick={handleDeleteClick}
                            />
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              )}
          </Card>
        </Flex>
      </Flex>

      <DeleteDialog
        isOpen={!!suiteToDelete}
        title="Delete Test Suite"
        description={`Are you sure you want to delete "${suiteToDelete?.name}"? This action cannot be undone.`}
        onConfirm={() => {
          if (suiteToDelete) {
            deleteSuite({ suiteId: suiteToDelete.id });
          }
        }}
        onCancel={() => setSuiteToDelete(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
